import React, { useState, useRef } from 'react'
import Classes from './Faq.module.css'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { motion, useInView } from 'framer-motion'
import { fadeUpOnView } from '../../../../../utils/motionVariants'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { SERVICE_AREA_NAMES } from '../../../ServicePages/serviceData'

const areaList = `${SERVICE_AREA_NAMES.slice(0, -1).join(', ')}, and ${SERVICE_AREA_NAMES[SERVICE_AREA_NAMES.length - 1]}`

// Used on the homepage (default questions) and on service/city pages, which
// pass their own `items` and output their own FAQPage schema.
export default function Faq({ items, withSchema = true }) {
    const [expanded, setExpanded] = useState('panel1')
    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false)
    }

    const homeFaqs = [
        {
            question: 'What types of services do you offer?',
            answer: 'We specialize in professional dryer vent cleaning to improve safety, airflow, and efficiency in your home. We also offer dependable appliance repair for common household appliances, including dryers, washers, refrigerators, and ovens, with fast and reliable service you can trust.',
        },
        {
            question: 'What areas do you serve?',
            answer: `We proudly serve homes across Palm Beach and Broward Counties, including ${areaList}.`,
        },
        {
            question: 'How often should I have my dryer vent cleaned?',
            answer: 'Most homes should have the dryer vent cleaned at least once a year. If you have a large household, pets, or a long vent run, or if you notice longer drying times, every six months is a good idea.',
        },
        {
            question: 'What are the signs of a clogged dryer vent?',
            answer: 'Common signs include clothes taking more than one cycle to dry, the dryer or laundry room feeling unusually hot, a burning smell, lint building up around the lint trap or dryer door, and a vent flap outside that barely opens when the dryer is running.',
        },
        {
            question: 'Can a clogged dryer vent cause a fire?',
            answer: 'Yes. Lint is highly flammable, and failure to clean is the leading cause of home clothes dryer fires according to the U.S. Fire Administration. Regular dryer vent cleaning removes built-up lint, lowers fire risk, and helps your dryer run more efficiently.',
        },
        {
            question: 'How much does dryer vent cleaning or appliance repair cost?',
            answer: 'Dryer vent cleaning is quoted upfront before we start, so there are no surprises. Appliance repairs begin with a service call, where our technician diagnoses the problem and gives you a clear quote before any repair work is done.',
        },
        {
            question: 'How do I schedule an appointment?',
            answer: <>Scheduling is quick and easy! You can book directly online by visiting this link: <a className={Classes.bookAppointText} href='https://book.housecallpro.com/book/Quick-Appliance-Services-and-Dryer-Vent-Cleaning/1d772c9908b14627bf68d004ecf2882d' target='_blank' rel='noreferrer' >Book an appointment</a> </>,
            schemaAnswer: 'Scheduling is quick and easy! You can book directly online at https://book.housecallpro.com/book/Quick-Appliance-Services-and-Dryer-Vent-Cleaning/1d772c9908b14627bf68d004ecf2882d',
        },
        {
            question: 'Are you licensed and insured?',
            answer: 'Yes. Quick Vents and Appliance Repair is fully licensed and insured, and we meet all Florida and local requirements. Our technicians treat your home with care and complete every dryer vent cleaning and appliance repair safely and to a high standard.',
        },
    ]

    const faqs = items || homeFaqs

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: { '@type': 'Answer', text: faq.schemaAnswer || faq.answer },
        })),
    }

    const sectionRef = useRef(null)
    const isSectionInView = useInView(sectionRef, { once: true, margin: '-100px' })

    return (
        <motion.section
            id="faq"
            className={Classes.section}
            ref={sectionRef}
            initial="hidden"
            animate={isSectionInView ? 'visible' : 'hidden'}
        >
            {withSchema && (
                <Helmet>
                    <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                </Helmet>
            )}
            <div>
                <div className={Classes.faqHeadings} >
                    <motion.div className={Classes.miniText} variants={fadeUpOnView(0)}>FAQs</motion.div>
                    <motion.h2 className={Classes.subheading} variants={fadeUpOnView(0.1)}>
                        Frequently Asked <br />Questions
                    </motion.h2>
                    <motion.p className={Classes.text} variants={fadeUpOnView(0.2)}>
                        Find answers to common questions about dryer vent cleaning or appliance repairs and booking your appointment.
                    </motion.p>
                    <motion.p className={`${Classes.text} ${Classes.contactLine}`} variants={fadeUpOnView(0.25)}>
                        Have additional questions?{' '}
                        <Link to="/contact" className={Classes.contactLink}>Contact us here</Link>.
                    </motion.p>
                </div>
            </div>

            <div id="questions" className={Classes.faqs}>
                <div className={Classes.faqsCon}>
                    {faqs.map((faq, index) => (
                        <motion.div
                            key={index}
                            variants={fadeUpOnView(Math.min(index, 3) * 0.05, 24)}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.1 }}
                            className={Classes.faqElem}
                        >
                            <Accordion
                                expanded={expanded === `panel${index + 1}`}
                                onChange={handleChange(`panel${index + 1}`)}
                                sx={{
                                    marginBottom: '1rem',
                                    borderRadius: '7px !important',
                                    padding: '6px 20px !important',
                                    boxShadow: '0px 0px 5px -2px #0000006b',
                                    '&:before': { display: 'none' },
                                }}
                                className={Classes.Accordion}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ fill: 'black' }} />}
                                    aria-controls={`panel${index + 1}bh-content`}
                                    id={`panel${index + 1}bh-header`}
                                    sx={{
                                        minHeight: '32px !important',
                                        margin: '0 !important',
                                        padding: '0px 10px !important',
                                        '&.Mui-expanded': {
                                            margin: '0 !important',
                                            minHeight: '32px !important',
                                            padding: '0px 10px !important',
                                        },
                                        '.MuiAccordionSummary-content': {
                                            margin: '10px 0 !important',
                                        },
                                    }}
                                >
                                    <div className={Classes.AccordionHeader}>
                                        <h5>{faq.question}</h5>
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails
                                    className={Classes.accordinBody}
                                    sx={{ padding: '8px 16px 16px 10px !important' }}
                                >
                                    <h5 className={Classes.accordionText}>{faq.answer}</h5>
                                </AccordionDetails>
                            </Accordion>
                        </motion.div>
                    ))}
                </div>
            </div>
        </motion.section >
    )
}
